import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import toast from 'react-hot-toast';
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isConnectionError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    const isConnectionError =
      error.message?.includes('Could not establish connection') ||
      error.message?.includes('Receiving end does not exist') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('NetworkError') ||
      error.message?.includes('Connection refused');

    return {
      hasError: true,
      isConnectionError,
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    toast.error('ErrorBoundary caught an error:', error, errorInfo);
    if (this.state.isConnectionError) {
      toast.warn('Connection error detected, likely from browser extension:', error.message);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { isConnectionError, retryCount } = this.state;
      const maxRetries = 3;

      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl"
          >
            <div className="mb-6">
              {isConnectionError ? (
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
                  <WifiOff className="h-8 w-8 text-orange-600" />
                </div>
              ) : (
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              )}
            </div>

            <h1 className="mb-2 text-2xl font-bold text-slate-900">
              {isConnectionError ? 'Connection Issue' : 'Something went wrong'}
            </h1>

            <p className="mb-6 text-slate-600">
              {isConnectionError ? (
                <>
                  We detected a connection issue. This might be caused by a browser extension or
                  network problem. The application should continue working normally.
                </>
              ) : (
                'An unexpected error occurred. Please try refreshing the page.'
              )}
            </p>

            {isConnectionError && (
              <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start space-x-3">
                  <Wifi className="mt-0.5 h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <h3 className="mb-1 text-sm font-medium text-blue-900">Quick Fix</h3>
                    <p className="text-xs text-blue-700">
                      Try disabling browser extensions temporarily or check your internet
                      connection.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {retryCount < maxRetries && (
                <button
                  onClick={this.handleRetry}
                  className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Try Again</span>
                </button>
              )}

              <button
                onClick={this.handleReload}
                className="w-full rounded-lg bg-slate-100 px-6 py-3 text-slate-700 transition-colors hover:bg-slate-200"
              >
                Reload Page
              </button>
            </div>

            {retryCount >= maxRetries && (
              <p className="mt-4 text-sm text-slate-500">
                If the problem persists, please contact support.
              </p>
            )}

            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 max-h-32 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-600">
                  {this.state.error?.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
